/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Ops Console dialogs, drawers and form controls for Swift's governed flows (request a cut-over,
 * approve it, roll back, readiness): a header with an icon tile, title and one line; a scrolling
 * body; a footer with the actions on the right. Inputs are the list toolbar's 34 px controls.
 */

import * as React from "react";
import styled, { css } from "styled-components";
import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";

import Modal from "@src/components/ui/Modal";
import IconTile from "@src/components/ui/IconTile";
import type { IconTileTone } from "@src/components/ui/IconTile/IconTile";
import { Portal, T9 } from "@src/components/ui/Lists/ListTable";

// ------------------------------------------------------------------ dialog

const DialogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(100vh - 32px);
`;
const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 16px 24px;
  border-bottom: 1px solid ${T9.slate200};
  flex-shrink: 0;
`;
const HeadText = styled.div`
  flex: 1;
  min-width: 0;
`;
const HeadTitle = styled.div`
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const HeadSubtitle = styled.div`
  margin-top: 2px;
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate500};
`;
const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${T9.slate500};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover {
    background: ${T9.slate100};
    color: ${T9.slate900};
  }
`;
const Body = styled.div<{ padded?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: ${props => (props.padded === false ? "0" : "20px 24px")};
  color: ${T9.slate700};
  font-size: 14px;
  line-height: 20px;
`;
const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid ${T9.slate200};
  background: ${T9.slate50};
  flex-shrink: 0;
  border-radius: 0 0 8px 8px;
`;
export const FootSpacer = styled.div`
  flex: 1;
`;
export const FootNote = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
`;

type DialogProps = {
  isOpen?: boolean;
  title: string;
  subtitle?: React.ReactNode;
  icon?: LucideIcon;
  tone?: IconTileTone;
  width?: number;
  onRequestClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** No padding around the body (tables, tabs). */
  flush?: boolean;
  testId?: string;
};

// The Modal clones its children with `onResizeUpdate`; this inner part takes it and ignores it.
const DialogInner = (props: DialogProps & { onResizeUpdate?: any }) => (
  <DialogWrapper data-test-id={props.testId || "opsDialog"}>
    <Head>
      {props.icon ? <IconTile icon={props.icon} size={36} tone={props.tone} /> : null}
      <HeadText>
        <HeadTitle data-test-id="opsDialog-title">{props.title}</HeadTitle>
        {props.subtitle ? <HeadSubtitle>{props.subtitle}</HeadSubtitle> : null}
      </HeadText>
      <CloseButton type="button" aria-label="Close" onClick={props.onRequestClose}>
        <X size={18} />
      </CloseButton>
    </Head>
    <Body padded={!props.flush}>{props.children}</Body>
    {props.footer ? <Foot>{props.footer}</Foot> : null}
  </DialogWrapper>
);

export const OpsDialog = (props: DialogProps) => (
  <Modal
    isOpen={props.isOpen !== false}
    contentLabel={props.title}
    contentWidth={`${props.width || 640}px`}
    onRequestClose={props.onRequestClose}
    topBottomMargin={16}
  >
    <DialogInner {...props} />
  </Modal>
);

// ------------------------------------------------------------------ drawer

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(15, 23, 42, 0.35);
`;
const DrawerPanel = styled.aside<{ width: number }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: ${props => props.width}px;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: var(--t9-bg-surface);
  box-shadow: -12px 0 32px -12px rgb(15 23 42 / 0.25);
  border-left: 1px solid ${T9.slate200};
  animation: ops-drawer-in 180ms cubic-bezier(0.4, 0, 0.2, 1);
  @keyframes ops-drawer-in {
    from {
      transform: translateX(24px);
      opacity: 0.4;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
`;
const DrawerFoot = styled(Foot)`
  border-radius: 0;
`;

type DrawerProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: LucideIcon;
  tone?: IconTileTone;
  width?: number;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
};
export const OpsDrawer = (props: DrawerProps) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.onClose]);
  return (
    <Portal>
      <Overlay onClick={props.onClose} />
      <DrawerPanel width={props.width || 600} role="dialog" aria-modal="true" data-test-id={props.testId || "opsDrawer"}>
        <Head>
          {props.icon ? <IconTile icon={props.icon} size={36} tone={props.tone} /> : null}
          <HeadText>
            <HeadTitle>{props.title}</HeadTitle>
            {props.subtitle ? <HeadSubtitle>{props.subtitle}</HeadSubtitle> : null}
          </HeadText>
          <CloseButton type="button" aria-label="Close" onClick={props.onClose}>
            <X size={18} />
          </CloseButton>
        </Head>
        <Body>{props.children}</Body>
        {props.footer ? <DrawerFoot>{props.footer}</DrawerFoot> : null}
      </DrawerPanel>
    </Portal>
  );
};

// ------------------------------------------------------------------ notices

export type NoticeTone = "info" | "success" | "warning" | "danger" | "neutral";
const NOTICE: { [t in NoticeTone]: { bg: string; border: string; fg: string; icon: LucideIcon } } = {
  info: { bg: "var(--t9-info-50)", border: "var(--t9-info-200)", fg: "var(--t9-info-700)", icon: Info },
  success: { bg: "var(--t9-selected-bg)", border: "var(--t9-brand-100)", fg: "var(--t9-primary)", icon: CircleCheck },
  warning: { bg: "var(--t9-info-50)", border: "var(--t9-accent-500)", fg: "var(--t9-info-700)", icon: TriangleAlert },
  danger: { bg: "var(--t9-danger-50)", border: "var(--t9-danger-200)", fg: "var(--t9-danger-700)", icon: CircleAlert },
  neutral: { bg: T9.slate50, border: T9.slate200, fg: T9.slate700, icon: Info },
};
const NoticeWrapper = styled.div<{ tone: NoticeTone }>`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${props => NOTICE[props.tone].border};
  background: ${props => NOTICE[props.tone].bg};
  color: ${props => NOTICE[props.tone].fg};
  font-size: 13px;
  line-height: 18px;
  > svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;
const NoticeBody = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;
const NoticeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;
const NoticeActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;
type NoticeProps = {
  tone?: NoticeTone;
  title?: React.ReactNode;
  icon?: LucideIcon;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  style?: React.CSSProperties;
  testId?: string;
};
export const Notice = ({ tone = "info", title, icon, children, actions, style, testId }: NoticeProps) => {
  const Icon = icon || NOTICE[tone].icon;
  return (
    <NoticeWrapper tone={tone} style={style} role={tone === "danger" ? "alert" : undefined} data-test-id={testId || "notice"}>
      <Icon size={16} />
      <NoticeBody>
        {title ? <NoticeTitle>{title}</NoticeTitle> : null}
        {children}
      </NoticeBody>
      {actions ? <NoticeActions>{actions}</NoticeActions> : null}
    </NoticeWrapper>
  );
};

// ------------------------------------------------------------------ form

export const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, minmax(0, 1fr));
  gap: 16px 16px;
`;
export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  & + & {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid ${T9.slate100};
  }
`;
export const SectionHeading = styled.div`
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;
const FieldWrapper = styled.label<{ span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  ${props => (props.span ? `grid-column: span ${props.span};` : "")}
`;
const FieldLabelRow = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;
export const FieldLabel = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: ${T9.slate800};
  .req {
    color: var(--t9-danger-700);
    margin-left: 2px;
  }
`;
export const FieldAside = styled.span`
  font-size: 12px;
  color: ${T9.slate500};
  white-space: nowrap;
`;
export const FieldHint = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
`;
export const FieldError = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: var(--t9-danger-700);
`;
type FieldProps = {
  label: React.ReactNode;
  required?: boolean;
  aside?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  span?: number;
  children: React.ReactNode;
  as?: "label" | "div";
};
export const FormField = (props: FieldProps) => (
  <FieldWrapper span={props.span} as={props.as}>
    <FieldLabelRow>
      <FieldLabel>
        {props.label}
        {props.required ? <span className="req">*</span> : null}
      </FieldLabel>
      {props.aside ? <FieldAside>{props.aside}</FieldAside> : null}
    </FieldLabelRow>
    {props.children}
    {props.error ? <FieldError role="alert">{props.error}</FieldError> : props.hint ? <FieldHint>{props.hint}</FieldHint> : null}
  </FieldWrapper>
);

const control = css<{ invalid?: boolean }>`
  box-sizing: border-box;
  height: 34px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid ${props => (props.invalid ? "var(--t9-danger-200)" : T9.slate300)};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  color: ${T9.slate900};
  padding: 0 10px;
  &::placeholder {
    color: ${T9.slate400};
  }
  &:focus {
    outline: none;
    border-color: ${T9.accent500};
    box-shadow: 0 0 0 2px ${T9.accent500};
  }
  &:disabled {
    background: ${T9.slate50};
    color: ${T9.slate500};
    cursor: not-allowed;
  }
`;
export const OpsInput = styled.input<{ invalid?: boolean }>`
  ${control}
  &[type="number"] {
    font-variant-numeric: tabular-nums;
  }
`;
export const OpsSelect = styled.select<{ invalid?: boolean }>`
  ${control}
  padding-right: 28px;
  cursor: pointer;
`;
export const OpsTextarea = styled.textarea<{ invalid?: boolean }>`
  ${control}
  height: auto;
  min-height: 76px;
  padding: 8px 10px;
  resize: vertical;
  line-height: 18px;
`;

const CheckRowWrapper = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  input {
    margin: 2px 0 0 0;
    width: 16px;
    height: 16px;
    accent-color: ${T9.brand600};
    flex-shrink: 0;
    cursor: inherit;
  }
`;
const CheckText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  .t {
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    color: ${T9.slate800};
  }
  .d {
    font-size: 12px;
    line-height: 16px;
    color: ${T9.slate500};
  }
`;
type CheckRowProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  testId?: string;
};
export const CheckRow = (props: CheckRowProps) => (
  <CheckRowWrapper disabled={props.disabled} data-test-id={props.testId}>
    <input
      type="checkbox"
      checked={props.checked}
      disabled={props.disabled}
      onChange={e => props.onChange(e.target.checked)}
    />
    <CheckText>
      <span className="t">{props.label}</span>
      {props.description ? <span className="d">{props.description}</span> : null}
    </CheckText>
  </CheckRowWrapper>
);

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const RadioCardWrapper = styled.label<{ selected?: boolean; danger?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  border: 1px solid
    ${props => (props.selected ? (props.danger ? "var(--t9-danger-200)" : T9.brand300) : T9.slate200)};
  background: ${props => (props.selected ? (props.danger ? "var(--t9-danger-50)" : T9.brand50) : "var(--t9-bg-surface)")};
  input {
    margin: 2px 0 0 0;
    width: 16px;
    height: 16px;
    accent-color: ${props => (props.danger ? "var(--t9-danger-700)" : T9.brand600)};
    flex-shrink: 0;
  }
  .t {
    color: ${props => (props.danger && props.selected ? "var(--t9-danger-700)" : T9.slate800)};
  }
  &:hover {
    border-color: ${props => (props.disabled ? T9.slate200 : props.danger ? "var(--t9-danger-200)" : T9.brand300)};
  }
`;
export type RadioOption = {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  icon?: LucideIcon;
};
type RadioCardsProps = {
  name: string;
  value: string;
  options: RadioOption[];
  onChange: (value: string) => void;
};
export const RadioCards = ({ name, value, options, onChange }: RadioCardsProps) => (
  <RadioGroup role="radiogroup">
    {options.map(o => {
      const Icon = o.icon;
      return (
        <RadioCardWrapper
          key={o.value}
          selected={o.value === value}
          danger={o.danger}
          disabled={o.disabled}
          data-test-id={`radio-${name}-${o.value}`}
        >
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={o.value === value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
          />
          <CheckText>
            <span className="t" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {Icon ? <Icon size={14} /> : null}
              {o.label}
            </span>
            {o.description ? <span className="d">{o.description}</span> : null}
          </CheckText>
        </RadioCardWrapper>
      );
    })}
  </RadioGroup>
);

// ------------------------------------------------------------------ chips

export const Chip = styled.button<{ selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  min-width: 40px;
  padding: 0 10px;
  border-radius: 9999px;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${props => (props.selected ? T9.brand600 : T9.slate300)};
  background: ${props => (props.selected ? T9.brand600 : "var(--t9-bg-surface)")};
  color: ${props => (props.selected ? "white" : T9.slate600)};
  &:hover {
    border-color: ${props => (props.selected ? T9.brand700 : T9.slate400)};
    color: ${props => (props.selected ? "white" : T9.slate900)};
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${T9.brand600};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: ${T9.brand700};
    text-decoration: underline;
  }
  &:disabled {
    color: ${T9.slate400};
    cursor: default;
    text-decoration: none;
  }
`;

export const Spinner = styled.span`
  display: inline-flex;
  svg {
    animation: ops-spin 1s linear infinite;
  }
  @keyframes ops-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
